// Die Login-Verlaufskette für den Dialog (#4519 / #4528).
//
// Alex: „vom Start des Tokens bis zu Login-Error … dann sehe ich wirklich, was
// passiert ist." Ein einzelnes Urteil sagt, WO wir stehen — die Kette sagt,
// wie wir dorthin kamen.
//
// Vertrag mit Login: absteigend (jüngster zuerst), `outcome` getrennt vom
// `step`, damit „gelaufen und gescheitert" von „gar nicht erst versucht"
// unterscheidbar bleibt.

export interface TokenStep {
  /** Epoch in Sekunden. */
  at: number;
  step: string;
  outcome?: string | null;
  detail?: string | null;
}

/** Ein zusammengefasster Eintrag: gleiche Schritt/Ausgang-Kombination in Folge
 *  wird zu einer Zeile mit Zähler. */
export interface CollapsedStep extends TokenStep {
  count: number;
  /** Zeitpunkt des ÄLTESTEN Vorkommens dieser Serie — für „4× zwischen … und …". */
  firstAt: number;
}

const STEP_LABEL: Record<string, string> = {
  probe: 'Prüfung',
  bot_seed: 'Token gesetzt',
  master_refresh: 'Token erneuert',
  transition: 'Zustandswechsel',
  restart: 'Neustart',
  reseed: 'Neu bespielt',
  refresh: 'Auffrischung',
  login_required: 'Anmeldung nötig',
  recovered: 'wieder da',
  moved: 'Knoten gewechselt',
};

const OUTCOME_LABEL: Record<string, string> = {
  ok: 'ok',
  failed: 'gescheitert',
  deferred: 'nicht versucht',
};

/** Unbekannte Werte werden DURCHGEREICHT, nicht verschluckt: ein neuer
 *  serverseitiger Schritt soll sichtbar sein, auch unübersetzt. */
export const stepLabel = (s: string) => STEP_LABEL[s] || s;
export const outcomeLabel = (o: string | null | undefined) => (o ? OUTCOME_LABEL[o] || o : '');

/**
 * Fasst AUFEINANDERFOLGENDE gleiche Schritte zusammen.
 *
 * Grund: Von zwölf Einträgen sind bei realen Agenten oft acht identische
 * Prüfungen im Sekundenabstand (gemessen an Automation, 2026-08-07). Zwölf
 * Zeilen zu zeigen, von denen acht dasselbe sagen, versteckt die zwei, auf die
 * es ankommt.
 *
 * Bewusst NUR aufeinanderfolgende: Kommt derselbe Schritt später wieder, ist
 * das ein eigenes Ereignis und keine Wiederholung.
 */
export function collapseSteps(steps: readonly TokenStep[]): CollapsedStep[] {
  const out: CollapsedStep[] = [];
  for (const s of steps) {
    const prev = out[out.length - 1];
    if (prev && prev.step === s.step && (prev.outcome ?? null) === (s.outcome ?? null)) {
      prev.count += 1;
      prev.firstAt = Math.min(prev.firstAt, s.at);
      // Ein Detail aus einem späteren Vorkommen ist besser als keines.
      if (!prev.detail && s.detail) prev.detail = s.detail;
      continue;
    }
    out.push({ ...s, count: 1, firstAt: s.at });
  }
  return out;
}
